import React from 'react';
import { generateId } from '../../utility/functions';
import Table from '../../table/Table';
import Col from '../../layout/Col';
import Button from '../../forms/Button';
import PageBreak from '../../layout/PageBreak';
import FormSetting from '../../layout/FormSetting';
import Checkbox from '../../forms/Checkbox';
import Input from '../../forms/Input';
import Select from '../../forms/Select';
import TextArea from '../../forms/TextArea';
import Heading from '../../forms/Heading';
import { QuestRoute } from '../../api/QuestRoutes';
import GetState from '../../GetState';
import Row from '../../layout/Row';
import LocationSelectors from '../../forms/formComponents/LocationSelectors';
import TerminalIDsSelector from '../../forms/formComponents/TerminalIDsSelector';
import { HSOptions } from '../../../constants/HSOptions';
import Radio from '../../forms/Radio';
import Preput from '../../forms/Preput';
import HamQuest from '../../api/HamQuestGiver';
import Form from '../../forms/Form';
import ColorInput from '../../forms/formComponents/ColorInput';
import ImageIndex from '../../forms/formComponents/ImageIndex';
import ManagementRow from '../../table/ManagementRow';
import ModalFactory from '../../Modal';
import UpgradeAndAutoSelect from '../../forms/formComponents/UpgradeAndAutoSelect';

const ReferralProgram = ({ ...props }) => {
    const [awards, refreshAwards] = GetState(QuestRoute.referral.awards, { loading: [] });

    const ReferralForm = ({ values, ...props }) => (
        <>
            <Input
                label='Name'
                name='name'
                required
            />
            <Preput
                label='Description'
                name='description'
            />
            <Input
                label='SKU'
                name='sku'
            />

            <Select
                label='Award Type'
                name='awardType'
                options={HSOptions.expressPass.awardItemType}
                required
            />

            {(values.awardType === 'Item' || values.awardType === 'DollarDiscItem') && (
                <Radio
                    label='Item'
                    name='kioskItemId'
                    getOptions={QuestRoute.kiosks.wash}
                    buildOptions={e => {
                        e.label = e.name
                        e.value = e.id
                    }}
                    required
                />
            )}

            {values.awardType !== 'Item' && (
                <Input
                    label='Value'
                    name='awardValue'
                    type='price'
                />
            )}

            <Input
                label='Expires'
                name='expires'
                type='number'
            />

            <UpgradeAndAutoSelect isAward={true} />

            <LocationSelectors />

            <TerminalIDsSelector />
        </>
    )

    const deleteRefAward = (ref) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${ref.name}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting Referral',
                body: `Deleting ${ref.name}`,
                callback: () => HamQuest.delete(QuestRoute.referral.edit(ref.awardId))
            },
            confirmed: {
                title: 'Success',
                body: `${ref.name} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshAwards()
        })
    }

    const editRefAward = (ref) => {
        // console.log(ref)
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${ref.name}`,
            body: (
                <Form
                    inputs={{ ...ref }}
                    onSubmit={e => HamQuest.put(QuestRoute.referral.edit(e.awardId), { ...e })
                        .then(() => {
                            refreshAwards();
                            ModalFactory.close(id);
                        })
                    }
                    text='Update'
                >
                    {({ values }) => <ReferralForm values={values} />}
                </Form>
            )
        })
    }

    const addNewRefAward = () => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add a new Referral`,
            body: (
                <Form
                    inputs={{
                        name: '',
                        description: '',
                        sku: '',
                        awardType: '',
                        kioskItemId: '',
                        expires: '',
                        allowUpgrades: '',
                        autoSelectItem: '',
                        locations: [],
                        terminalIds: [],
                    }}
                    onSubmit={e => HamQuest.post(QuestRoute.referral.awards, { ...e })
                        .then(() => {
                            refreshAwards();
                            ModalFactory.close(id);
                        })
                    }
                    text='Update'
                >
                    {({ values }) => <ReferralForm values={values} />}
                </Form>
            )
        })
    }



    return (
        <PageBreak>

            <Col>
                <Heading>Referral Awards</Heading>
                <Col>
                    <span>The Referral Program allows your users to invite friends and family members to download the app. Each user has a unique ‘Referral Code’ that they can share via text message, email, Facebook etc.</span>
                    <span>When a potential user (RECIPIENT) is sent a referral by an existing app user (REFERRER), they download the app and enter the Referral Code at sign-up. After their first purchase is made, they are automatically sent an award that you determine below. This can be a complimentary wash, an Item Dollar Discount, or a Transaction Dollar Discount.</span>
                    <span>The Referrer will also receive an award of Loyalty Points (determined in Referral Settings) once the Recipient has made their first purchase. By offering both the Referrer and the Recipient ‘awards’, you can actively encourage users to market your car wash.</span>
                    <span>REFERRAL PROGRAM REQUIRES APP VERSION 4.0.0 OR HIGHER</span>
                </Col>
            </Col>

            <Table
                config={{
                    label: 'Referral Awards',
                    controls: [<Button onClick={addNewRefAward}>Add New Referral Award</Button>],
                    data: awards,
                    headers: [
                        {
                            label: 'Name',
                            propKey: 'name',
                        }, {
                            label: 'Description',
                            propKey: 'description ',
                        }, {
                            label: 'Award Type',
                            propKey: 'awardType',
                            options: true
                        }, {
                            label: 'SKU',
                            propKey: 'sku',
                        }, {
                            label: 'Item',
                            propKey: 'kioskItemName',
                            options: true
                        },
                    ],
                    renderFormat: (e) => (
                        <ManagementRow
                            key={generateId()}
                            controls={[
                                <Button key={generateId()} onClick={() => editRefAward(e)}>Edit</Button>,
                                <Button key={generateId()} onClick={() => deleteRefAward(e)}>Delete</Button>
                            ]}
                        >
                            <td>{e.name}</td>
                            <td>{e.description}</td>
                            <td>{e.awardType}</td>
                            <td>{e.sku}</td>
                            <td>{e.kioskItemName}</td>
                        </ManagementRow>
                    )
                }}
            />

            <FormSetting
                name='referralSettings'
                label='Settings'
                getInputs={QuestRoute.referral.settings}
                onSubmit={e => HamQuest.put(QuestRoute.referral.settings, { ...e })}
                editable
            >
                {({ values }) => (
                    <PageBreak>
                        <>
                            <Row wrap>
                                <Select
                                    label='Recipient Award'
                                    name='refereeAwardId'
                                    // getOptions={QuestRoute.referral.awards}
                                    // buildOptions={e => {
                                    //     e.value = e.awardId
                                    //     e.label = e.name
                                    // }}
                                    options={awards.map(e => ({ ...e, value: e.awardId, label: e.name }))}
                                    required
                                    nullable
                                />
                                <Input
                                    label='Referrer Points'
                                    name='referrerPoints'
                                    type='number'
                                    required
                                />
                            </Row>



                            <ColorInput name='logoRGB' required />

                            <ImageIndex colorName='logoRGB' />

                            <Input
                                label='Title'
                                name='features.ReferralProgram.Title'
                                required
                            />

                            <Preput
                                label='Description'
                                name='features.ReferralProgram.Description'
                                required
                            />

                            <Preput
                                label='Share Message'
                                name='shareMessage'
                                required
                            />

                            <Checkbox
                                label='Use Merchant Url'
                                name='useMerchantUrl'
                            />

                            <Input
                                label='Merchant URL'
                                name='merchantUrl'
                                disable={!values.useMerchantUrl}
                                required={values.useMerchantUrl}
                            />
                        </>

                        <>
                            <Heading>Recipient Signup Notification</Heading>
                            <Input
                                label='Notification Title'
                                name='refereeSignupNotificationTitle'
                                required
                            />
                            <Preput
                                label='Notification Body'
                                name='refereeSignupNotificationBody'
                                tags={[
                                    'Referrer.FirstName',
                                    'Referrer.LastName',
                                    'Referrer.FullName',
                                    'Recipient.FirstName',
                                    'Recipient.LastName',
                                    'Recipient.FullName',
                                    'Recipient.AwardName',
                                    'Account.DisplayName',
                                    'KioskItemName',
                                    'ExpirationDate',
                                ]}
                                required
                            />
                        </>

                        <>
                            <Heading>Recipient First Purchase Notification</Heading>
                            <Input
                                label='Notification Title'
                                name='refereeFirstPurchaseNotificationTitle'
                                required
                            />
                            <Preput
                                label='Notification Body'
                                name='refereeFirstPurchaseNotificationBody'
                                tags={[
                                    'Referrer.FirstName',
                                    'Referrer.LastName',
                                    'Referrer.FullName',
                                    'Recipient.FirstName',
                                    'Recipient.LastName',
                                    'Recipient.FullName',
                                    'Recipient.AwardName',
                                    'Account.DisplayName',
                                    'KioskItemName',
                                    'ExpirationDate',
                                ]}
                                required
                            />
                        </>

                        <>
                            <Heading>Referrer Signup Notification</Heading>
                            <Input
                                label='Notification Title'
                                name='referrerSignupNotificationTitle'
                                required
                            />
                            <Preput
                                label='Notification Body'
                                name='referrerSignupNotificationBody'
                                tags={[
                                    'Referrer.FirstName',
                                    'Referrer.LastName',
                                    'Referrer.FullName',
                                    'Recipient.FirstName',
                                    'Recipient.LastName',
                                    'Recipient.FullName',
                                    'Recipient.AwardName',
                                    'Account.DisplayName',
                                    'KioskItemName',
                                    'ExpirationDate',
                                ]}
                                required
                            />
                        </>

                        <>
                            <Heading>Referrer First Purchase Notification</Heading>
                            <Input
                                label='Notification Title'
                                name='referrerFirstPurchaseNotificationTitle'
                                required
                            />
                            <Preput
                                label='Notification Body'
                                name='referrerFirstPurchaseNotificationBody'
                                tags={[
                                    'Referrer.FirstName',
                                    'Referrer.LastName',
                                    'Referrer.FullName',
                                    'Recipient.FirstName',
                                    'Recipient.LastName',
                                    'Recipient.FullName',
                                    'Recipient.AwardName',
                                    // 'CompanyName',
                                    'KioskItemName',
                                    'ExpirationDate',
                                ]}
                                required
                            />
                        </>
                    </PageBreak>
                )}
            </FormSetting>
        </PageBreak >

    );
}

export default ReferralProgram;